import axios from 'axios';
import { BASE_URL } from '../constants/api-urls';

export interface ResetResponse {
  success: boolean;
  message: string;
  data: {
    playlistId: string;
    songsProcessed: number;
    positionsUpdated: number;
    timestamp: string;
  };
  errors: string[];
}

export interface ResetRequest {
  playlistId: string;
  userId: string;
  accessToken: string;
}

/**
 * Reset playlist positions using the new backend-centric approach
 * @param request - Reset request parameters
 * @returns Promise<ResetResponse>
 */
export const resetPlaylistPositions = async (
  request: ResetRequest
): Promise<ResetResponse> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/playlist/reset-positions`,
      {
        playlistId: request.playlistId,
        userId: request.userId,
        accessToken: request.accessToken
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error resetting playlist positions:', error);
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      throw new Error(error?.response?.data?.error || error?.response?.data?.message || 'Failed to reset playlist positions');
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error('Failed to reset playlist positions');
    }
  }
};

/**
 * Check if a playlist has locked positions that can be reset
 * @param playlistId - Spotify playlist ID
 * @param userId - User ID
 * @param accessToken - Access token for API calls
 * @returns Promise<boolean>
 */
export const hasLockedPositions = async (
  playlistId: string,
  userId: string,
  accessToken: string
): Promise<boolean> => {
  try {
    const response = await axios.get(
      `${BASE_URL}/getrequestsbysonguri/${userId}/${playlistId}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    const lockedSongs = response.data.data?.filter(
      (song: any) => song.lockPosition === true
    );

    return lockedSongs && lockedSongs.length > 0;
  } catch (error) {
    console.error('Error checking locked positions:', error);
    return false;
  }
}; 